"use client";

import { fillPageToken, type IntakePageDraft } from "@repo/questionnaire";

import { BrokerAvatar } from "@/components/brokers/broker-avatar";

export type IntakePageBrand = {
  name: string;
  avatarUrl?: string | null;
  licenseNumber?: string | null;
  logoUrl?: string | null;
  logoAlt?: string | null;
  licenseBadge?: string | null;
};

export function IntakePageCard({
  page,
  brand,
}: {
  page: Pick<IntakePageDraft, "headline" | "bullets" | "showTrust">;
  brand: IntakePageBrand;
}) {
  const headline = fillPageToken(page.headline, brand.name);
  const bullets = page.bullets
    .map((bullet) => fillPageToken(bullet, brand.name))
    .filter(Boolean);
  const license = brand.licenseNumber?.trim();
  const licenseBadge = brand.licenseBadge?.trim();
  const logoUrl = brand.logoUrl?.trim();
  const licenseLine = licenseBadge || (license ? `License ${license}` : null);

  return (
    <div className="flex flex-col items-center text-center">
      {page.showTrust ? (
        <div className="mb-5 flex flex-col items-center gap-3">
          <BrokerAvatar
            id={brand.name}
            name={brand.name}
            image={brand.avatarUrl}
            showVerified={false}
            size="xl"
          />
          <div>
            <p className="text-base font-medium text-foreground">{brand.name}</p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Business broker
            </p>
          </div>
        </div>
      ) : null}
      <h2 className="max-w-md text-balance text-2xl font-semibold tracking-tight">
        {headline}
      </h2>
      {bullets.length > 0 ? (
        <ul className="mt-5 max-w-md space-y-2 text-left text-base leading-relaxed text-foreground">
          {bullets.map((bullet) => (
            <li key={bullet} className="flex gap-2">
              <span aria-hidden className="mt-2 size-1.5 shrink-0 rounded-full bg-foreground" />
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {logoUrl || licenseLine ? (
        <div className="mt-8 flex max-w-md items-center justify-center gap-3">
          {logoUrl ? (
            // biome-ignore lint/performance/noImgElement: brokerage logo is a remote brand asset
            <img
              src={logoUrl}
              alt={brand.logoAlt?.trim() || ""}
              className="h-8 max-w-[10rem] object-contain"
            />
          ) : null}
          {logoUrl && licenseLine ? (
            <span aria-hidden className="h-8 w-px shrink-0 bg-border" />
          ) : null}
          {licenseLine ? (
            <p className="text-sm font-medium text-muted-foreground">{licenseLine}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
